import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import API_BASE from '../config';

const RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4', '3:2', '2:3'];

function useVideoPoller(token, requestId, onDone, onError) {
  useEffect(() => {
    if (!requestId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/poll/${requestId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await res.json();
        if (!data.success) { clearInterval(interval); onError(data.error); return; }
        if (data.status === 'done')   { clearInterval(interval); onDone(data.url); }
        if (data.status === 'failed') { clearInterval(interval); onError('영상 생성 실패'); }
      } catch (e) {
        clearInterval(interval);
        onError(e.message);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [requestId]);
}

export default function RefToVideo({ onSave }) {
  const { token } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [resolution, setResolution] = useState('720p');
  const [duration, setDuration] = useState('10');
  const [ratio, setRatio] = useState('16:9');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const ACCENT = 'var(--accent5)';

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, [loading]);

  useVideoPoller(
    token,
    requestId,
    (url) => { setVideoUrl(url); setLoading(false); onSave({ type: 'r2v', prompt, url }); },
    (err) => { setError(err); setLoading(false); }
  );

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = e => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!imageFile || !prompt.trim()) return;
    setLoading(true);
    setError(null);
    setVideoUrl(null);
    setRequestId(null);
    setElapsed(0);
    try {
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = e => res(e.target.result);
        reader.onerror = rej;
        reader.readAsDataURL(imageFile);
      });
      const response = await fetch(`${API_BASE}/api/r2v`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ prompt, image: base64, resolution, duration, ratio }),
      });
      const data = await response.json();
      if (data.success) { setRequestId(data.request_id); }
      else { setError(data.error); setLoading(false); }
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const cancel = () => { setRequestId(null); setLoading(false); setElapsed(0); };

  const download = () => {
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = `grok-r2v-${Date.now()}.mp4`;
    a.click();
  };

  const fmtTime = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="panel">
      <div className="panel-header">
        <div className="panel-icon orange">🎭</div>
        <div className="panel-title-group">
          <div className="panel-title">참조 → 영상</div>
          <div className="panel-sub">얼굴/캐릭터를 참조해서 새로운 영상 생성</div>
        </div>
      </div>
      <div className="panel-body">
        <div className="form-col">
          <div className="info-box">
            💡 이미지가 첫 프레임이 아닌 <strong style={{ color: ACCENT }}>참조</strong>로만 사용돼요.
            얼굴이나 캐릭터를 유지한 채 새로운 영상을 만들 수 있어요.
          </div>

          <div className="form-group">
            <label>참조 이미지</label>
            <div
              className={`upload-area ${dragging ? 'dragging' : ''}`}
              style={{
                borderColor: dragging ? ACCENT : undefined,
                background: dragging ? 'rgba(255,149,0,0.05)' : undefined,
              }}
              onClick={() => fileRef.current.click()}
              onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
            >
              <input type="file" accept="image/*" ref={fileRef} style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])} />
              {imagePreview ? (
                <img src={imagePreview} alt="preview" className="upload-preview" />
              ) : (
                <>
                  <span className="upload-icon">🎭</span>
                  <span className="upload-text">참조할 이미지를 올리세요</span>
                  <span className="upload-hint">PNG, JPG, WEBP</span>
                </>
              )}
            </div>
            {imagePreview && (
              <button className="remove-btn" onClick={() => { setImageFile(null); setImagePreview(null); }}>
                ✕ 이미지 제거
              </button>
            )}
          </div>

          <div className="form-group">
            <label>프롬프트 (필수)</label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="이 사람이 해변을 걷고 있어, 노을이 지는 배경..."
              rows={4}
            />
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>해상도</label>
              <select value={resolution} onChange={e => setResolution(e.target.value)}>
                <option value="480p">480p</option>
                <option value="720p">720p (최대)</option>
              </select>
            </div>
            <div className="form-group">
              <label>길이</label>
              <select value={duration} onChange={e => setDuration(e.target.value)}>
                <option value="5">5초</option>
                <option value="10">10초 (최대)</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>화면 비율</label>
            <div className="ratio-grid">
              {RATIOS.map(r => (
                <button
                  key={r}
                  className="ratio-btn"
                  onClick={() => setRatio(r)}
                  style={{
                    border: ratio === r ? `2px solid ${ACCENT}` : '1px solid var(--border)',
                    background: ratio === r ? 'rgba(255,149,0,0.12)' : 'var(--bg3)',
                    color: ratio === r ? ACCENT : 'var(--text2)',
                    fontWeight: ratio === r ? '700' : '400',
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {!loading ? (
            <button
              className="btn btn-orange"
              onClick={generate}
              disabled={!imageFile || !prompt.trim()}
            >
              🎭 영상 생성
            </button>
          ) : (
            <button className="btn-cancel" onClick={cancel}>✕ 취소</button>
          )}
          {error && <div className="error-box">⚠ {error}</div>}
        </div>

        <div className="output-area">
          <span className="output-label">결과</span>
          <div className="output-box">
            {loading ? (
              <div className="loading">
                <div className="loading-timer">{fmtTime(elapsed)}</div>
                <div className="loading-bar">
                  <div className="loading-bar-fill" style={{ background: ACCENT }} />
                </div>
                <span className="loading-text">영상 생성 중... (보통 1~4분 소요)</span>
              </div>
            ) : videoUrl ? (
              <video src={videoUrl} controls autoPlay loop className="output-video" />
            ) : (
              <div className="output-placeholder">
                <span className="output-placeholder-icon">🎭</span>
                <span className="output-placeholder-text">영상이 여기 표시됩니다</span>
              </div>
            )}
          </div>
          {videoUrl && <button className="btn-download" onClick={download}>↓ MP4 다운로드</button>}
        </div>
      </div>
    </div>
  );
}
